import React, { forwardRef, useState } from 'react';

import { Datepicker } from '@mobiscroll/react';
import moment from 'moment/moment';

import DatePickerCustomHeader from '../DatePicker/components/DatePickerCustomHeader/DatePickerCustomHeader';

import { DatePickerStyled } from './DatePicker.style';
import { renderDatePickerVariant } from './DatePickerInputVariant';

import type { MbscDateType } from '@mobiscroll/react';
import type { Moment } from 'moment';

type DatePickerProps = {
  id?: string;
  variant?: 'iconButton' | 'input' | 'button';
  isDisabled?: boolean;
  isRequired?: boolean;
  isReadOnly?: boolean;
  isFullWidth?: boolean;
  isShowIcon?: boolean;
  isShowDefaultStyle?: string;
  isError?: boolean;
  label?: string;
  placeholder?: string;
  value?: string;
  helperText?: string;
  errorMessage?: string;
  size?: 'small' | 'medium';
  inputName?: string;
  dateFormat?: string;
  onChange: (date: Moment | null) => void;
  isClearable?: boolean;
  isLate?: boolean;
  renderCalendarHeader?: () => JSX.Element;
  max?: MbscDateType;
  [x: string]: any;
};

export const DatePicker = forwardRef<HTMLHeadingElement, DatePickerProps>((props, ref) => {
  const [isShowPopup, setIsShowPopup] = useState(false);
  const {
    id,
    isDisabled,
    isShowIcon = true,
    inputName = 'Date',
    isError,
    isFullWidth = true,
    isRequired,
    isReadOnly = false,
    errorMessage,
    label,
    placeholder,
    isShowDefaultStyle,
    isClearable,
    helperText,
    value,
    size,
    dateFormat,
    variant = 'input',
    isLate,
    onChange,
    renderCalendarHeader,
    max,
    ...args
  } = props;
  const onOpenPopup = () => {
    setIsShowPopup(true);
  };
  const onClosePopup = () => {
    setIsShowPopup(false);
  };

  const handleChange = (val: any) => {
    onChange(val.value);
  };

  return (
    <DatePickerStyled id={id || 'datePicker'}>
      <Datepicker
        {...args}
        cssClass={isShowDefaultStyle ? '' : 'date-picker'}
        inputRef={ref}
        themeVariant="light"
        moment={moment}
        onChange={handleChange}
        onOpen={onOpenPopup}
        onClose={onClosePopup}
        firstDay={1}
        defaultValue={value}
        value={value}
        focusOnClose={false}
        disabled={isDisabled}
        max={max}
        renderCalendarHeader={
          isShowDefaultStyle
            ? undefined
            : renderCalendarHeader ||
              (() => (
                <DatePickerCustomHeader
                  inputName={inputName}
                  placeholder={placeholder}
                  isClearable={isClearable}
                  onChange={onChange}
                  value={value}
                />
              ))
        }
        dayNamesMin={['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']}
        monthNamesMin={['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']}
        controls={['calendar']}
        theme="ios"
        returnFormat="moment"
        dateFormat={dateFormat || 'MMM DD YYYY'}
        touchUi={false}
        inputComponent={renderDatePickerVariant}
        inputProps={{
          isShowIcon,
          isClearable,
          label,
          errorMessage,
          isFullWidth,
          isDisabled,
          isReadOnly,
          isRequired,
          onChange,
          isError,
          variant,
          placeholder,
          helperText,
          size,
          value,
          isShowPopup,
          dateFormat,
          isLate,
        }}
      />
    </DatePickerStyled>
  );
});
